import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { wasteExceedsLeft, type IArrivedFigures } from '@d-orca/shared/client/logistics';
import { parseNumber } from '@d-orca/shared/client/utils';
import { shownUnit } from './stockUnits';
import type { IUnitMismatchActions } from './StockEntryDialog.types';
import styles from './StockEntryDialog.module.css';

interface IStockEntrySyncFieldsProps {
  isSync: boolean;
  remaining: string;
  onRemainingChange: (value: string) => void;
  remainingClassName: string;
  remainingSeedNote: ReactNode;
  waste: string;
  onWasteChange: (value: string) => void;
  wasteClassName: string;
  wasteSeedNote: ReactNode;
  unit: string;
  hasArrival: boolean;
  arrivedKnown: number | null;
  arrivedQty: string;
  onArrivedQtyChange: (value: string) => void;
  arrived?: IArrivedFigures | null;
  unitMismatch: boolean;
  merged: number | null;
  remainingValid: boolean;
  remainingNum: number;
  arrivedValid: boolean;
  arrivedNum: number;
  /** The material being synced — named by the recount link. */
  material: string;
  /** The ways out of a cross-unit merge (`IUnitMismatchActions`). */
  actions?: IUnitMismatchActions;
  t: TFunction;
}

interface IEstimatedFigureFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  className: string;
  seedNote: ReactNode;
  unit: string;
  t: TFunction;
}

/** A seeded, editable quantity with the stock's unit locked beside it and the
 *  "estimated — correct it" footnote under it. The ask is red so it reads as
 *  an instruction, not a footnote. */
function EstimatedFigureField({
  id,
  label,
  value,
  onChange,
  className,
  seedNote,
  unit,
  t,
}: IEstimatedFigureFieldProps) {
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <div className={styles.rowFields}>
        <input
          id={id}
          className={className}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputMode="decimal"
          autoComplete="off"
        />
        <span className={`${styles.input} ${styles.lockedField}`}>{unit}</span>
      </div>
      <span className={styles.fieldHint}>
        {t('logistics.stockDialog.remainingWhy')}
        <span className={styles.fieldAsk}>{t('logistics.stockDialog.remainingCorrect')}</span>
      </span>
      {seedNote}
    </div>
  );
}

/** Sync-only fields: the old stock, what landed on top of it (a fact from the
 *  delivery record, or typed when the record has none), and the total they
 *  make — computed, never editable directly. */
export function StockEntrySyncFields({
  isSync,
  remaining,
  onRemainingChange,
  remainingClassName,
  remainingSeedNote,
  waste,
  onWasteChange,
  wasteClassName,
  wasteSeedNote,
  unit,
  hasArrival,
  arrivedKnown,
  arrivedQty,
  onArrivedQtyChange,
  arrived,
  unitMismatch,
  merged,
  remainingValid,
  remainingNum,
  arrivedValid,
  arrivedNum,
  material,
  actions,
  t,
}: IStockEntrySyncFieldsProps) {
  if (!isSync) return null;
  // Said here, before Save, rather than by the server after it (QA 2026-10-18).
  const wasteTooBig = remainingValid && wasteExceedsLeft(waste.trim() === '' ? null : parseNumber(waste), remainingNum);
  return (
    <>
      {/* The correction is the point. Left uncorrected the count is
          dailyRate × daysLeft — the estimate restated — and the cycle it
          closes teaches the system what it already assumed. Waste shares the
          footnote: its seed (a tenth of the batch, capped at what is left)
          is an estimate to be checked, not a fact. */}
      <EstimatedFigureField
        id="stock-remaining"
        label={t('logistics.stockDialog.remaining')}
        value={remaining}
        onChange={onRemainingChange}
        className={remainingClassName}
        seedNote={remainingSeedNote}
        unit={unit}
        t={t}
      />

      <EstimatedFigureField
        id="stock-waste"
        label={t('logistics.stockDialog.waste')}
        value={waste}
        onChange={onWasteChange}
        className={wasteClassName}
        seedNote={wasteSeedNote}
        unit={unit}
        t={t}
      />
      {wasteTooBig ? (
        <span className={styles.fieldWarn} role="alert">
          {t('logistics.errors.wasteExceedsRemaining')}
        </span>
      ) : null}

      {hasArrival ? (
        <>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="stock-arrived">
              {t('logistics.stockDialog.arrived')}
            </label>
            <div className={styles.rowFields}>
              {arrivedKnown != null ? (
                // A known fact from the delivery record, not editable — faded
                // (locked), never amber. Amber is for estimates the user should
                // check; this is neither an estimate nor touchable (like Material).
                <span className={`${styles.input} ${styles.lockedField}`}>{arrivedKnown}</span>
              ) : (
                <input
                  id="stock-arrived"
                  className={styles.input}
                  value={arrivedQty}
                  onChange={(e) => onArrivedQtyChange(e.target.value)}
                  inputMode="decimal"
                  autoComplete="off"
                />
              )}
              <span className={`${styles.input} ${styles.lockedField}`}>{shownUnit(t, arrived?.unit) ?? unit}</span>
            </div>
            {/* Three stories for the arrived figure: recorded (locked),
                recorded in DIFFERENT units (ask for the total in the
                stock's unit — a cross-unit sum is not a number), or never
                recorded at all. The mixed case used to fall into the
                "didn't record how much" copy, which is false — they
                recorded plenty, they just disagree. */}
            <span className={styles.fieldHint}>
              {t(
                arrivedKnown != null
                  ? 'logistics.stockDialog.arrivedWhy'
                  : arrived?.mixedUnits
                    ? 'logistics.stockDialog.arrivedMixedWhy'
                    : 'logistics.stockDialog.arrivedUnknownWhy',
                { refs: arrived?.refs.join(', ') ?? '', unit },
              )}
            </span>
            {/* Adding sheets to bundles would produce a confident,
                meaningless total — say so rather than quietly summing. */}
            {unitMismatch ? (
              <span className={styles.fieldWarn}>
                {t('logistics.stockDialog.unitMismatch', {
                  delivery: shownUnit(t, arrived?.unit),
                  stock: unit,
                })}
                {actions && arrived ? (
                  <UnitMismatchLinks arrived={arrived} material={material} actions={actions} t={t} />
                ) : null}
              </span>
            ) : null}
          </div>

          <div className={styles.field}>
            <label className={styles.label}>{t('logistics.stockDialog.newAmount')}</label>
            <div className={styles.rowFields}>
              {/* Computed total (left + arrived), not editable — faded/locked
                  like Arrived and the units, never amber (amber is for
                  estimates you can correct). */}
              <span className={`${styles.input} ${styles.lockedField}`}>
                {merged ?? '—'}
              </span>
              <span className={`${styles.input} ${styles.lockedField}`}>{unit}</span>
            </div>
            <span className={styles.fieldHint}>
              {unitMismatch
                ? t('logistics.stockDialog.newAmountUnitMismatch')
                : t('logistics.stockDialog.newAmountWhy', {
                    left: remainingValid ? remainingNum : 0,
                    arrived: arrivedValid ? arrivedNum : 0,
                  })}
            </span>
          </div>
        </>
      ) : null}
    </>
  );
}

interface IUnitMismatchLinksProps {
  arrived: IArrivedFigures;
  material: string;
  actions: IUnitMismatchActions;
  t: TFunction;
}

/** "Open DEL-213 · Count Cement in kg" — the two ways out of a cross-unit
 *  merge. The order link only when one order landed: several are opened from
 *  the board, not guessed at. */
function UnitMismatchLinks({ arrived, material, actions, t }: IUnitMismatchLinksProps) {
  const onlyRef = arrived.refs.length === 1 ? arrived.refs[0] : undefined;
  return (
    <span className={styles.mismatchLinks}>
      {onlyRef !== undefined ? (
        <button type="button" className={styles.mismatchLink} onClick={() => actions.onOpenOrder(onlyRef)}>
          {t('logistics.stockDialog.unitMismatchOpenOrder', { ref: onlyRef })}
        </button>
      ) : null}
      <button type="button" className={styles.mismatchLink} onClick={() => actions.onCountInOtherUnit(material)}>
        {t('logistics.stockDialog.unitMismatchRecount', { material, unit: shownUnit(t, arrived.unit) })}
      </button>
    </span>
  );
}
